'use client'

import { IonApp, setupIonicReact } from '@ionic/react'
import { createBrowserHistory } from 'history'
import dynamic from 'next/dynamic'
import { wrapHistory } from 'oaf-react-router'
import { useEffect } from 'react'
import { Router } from 'react-router-dom'

import { config } from '@/config'
import { useCurrentUser } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { SettingUserInfoModalProvider } from '@/core/membership/useSettingUserInfoModal'
import { SettingMyFavoriteModalProvider } from '@/core/myFavorite/useSettingMyFavoriteModal'
import { Device, StatusBar } from '@/core/native'
import { useAppVersion } from '@/core/native/app'
import { handleChunkLoadError } from '@/lib/errorHandler'
import { setTags } from '@/lib/sentry'

import AppUrlListener from './DeepLink'
import { NativeOnlyRouter } from './NativeOnlyRouter'

setupIonicReact({
  mode: 'ios',
})

const history = createBrowserHistory()
wrapHistory(history)

const OrganizationRouter = dynamic(
  config.applicationType === 'organization'
    ? import('./OrganizationRouter')
        .then(m => m.OrganizationRouter)
        .catch(async error => {
          await handleChunkLoadError(error)
          return () => null
        })
    : Promise.resolve(() => null),
  { ssr: false }
)

const StandaloneRouter = dynamic(
  config.applicationType === 'standalone'
    ? import('./StandaloneRouter')
        .then(m => m.StandaloneRouter)
        .catch(async error => {
          await handleChunkLoadError(error)
          return () => null
        })
    : Promise.resolve(() => null),
  { ssr: false }
)

export const AppRouter = () => {
  const { deviceInfo } = Device.useDeviceInfo()
  const { analytics } = useAnalytics()
  const appVersion = useAppVersion()
  const { user, loading: userLoading, userClaims } = useCurrentUser()
  const isConsole = !!userClaims?.orgs?.[config.organizationId]?.artist

  useEffect(() => {
    StatusBar.setStatusBarStyle(deviceInfo?.platform ?? '', config.theme.dark)
  }, [deviceInfo?.platform])

  useEffect(() => {
    if (user) {
      const providerId = user.providers?.[0]?.providerId
      analytics?.track('login', {
        loginMethod: providerId === 'password' ? 'email' : providerId,
        applicationKey: config.applicationKey,
        isAnonymous: user.isAnonymous,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // add sentry tags
  useEffect(() => {
    setTags({
      userId: user?.uid,
      appVersion,
      liveupdate: config.buildId.slice(0, 6),
    })
  }, [user?.uid, appVersion])

  if (userLoading || !deviceInfo?.platform) {
    return null
  }

  /**
   * ネイティブのみのアプリをWebで開いた場合(例：Diica, Playdeck)
   * ログイン画面と管理画面のみのルーターに遷移する
   *
   * diicaはWebなしSTG環境ではNativeOnlyRouterに遷移する
   * playdeckでも同様の処理を追加
   */
  if (
    !config.platform.includes('web') &&
    deviceInfo?.platform === 'web' &&
    !isConsole &&
    (config.environment === 'production' ||
      location.host === 'diica-stg-webview.netlify.app' ||
      location.host === 'playdeck-stg.netlify.app')
  ) {
    return (
      <IonApp>
        <Router history={history}>
          <AppUrlListener />
          <NativeOnlyRouter user={user} isConsole={isConsole} />
        </Router>
      </IonApp>
    )
  }

  if (config.applicationType === 'standalone') {
    return (
      <IonApp>
        <Router history={history}>
          <AppUrlListener />
          <SettingUserInfoModalProvider>
            <SettingMyFavoriteModalProvider>
              <StandaloneRouter
                isArtist={!!userClaims?.orgs?.[config.organizationId]?.artist}
                user={user}
                isConsole={isConsole}
              />
            </SettingMyFavoriteModalProvider>
          </SettingUserInfoModalProvider>
        </Router>
      </IonApp>
    )
  }
  return (
    <IonApp>
      <Router history={history}>
        <AppUrlListener />
        <SettingUserInfoModalProvider>
          <SettingMyFavoriteModalProvider>
            <OrganizationRouter
              isArtist={!!userClaims?.orgs?.[config.organizationId]?.artist}
              user={user}
              isConsole={isConsole}
            />
          </SettingMyFavoriteModalProvider>
        </SettingUserInfoModalProvider>
      </Router>
    </IonApp>
  )
}
